/* eslint func-style: "off" */
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
  // Action types
  FETCH_DEFAULTS,
  CREATE_DEFAULT,
  PATCH_DEFAULT,
  DELETE_DEFAULT,
  SET_PLATFORM_FILTER,
  SET_PAGE,
  SET_PAGE_SIZE,
  LOOKUP_LOCATION_FORWARD,
  LOOKUP_LOCATION_REVERSE,
  // Action creators
  fetchDefaults,
  fetchDefaultsSucceeded,
  fetchDefaultsFailed,
  createDefaultSucceeded,
  createDefaultFailed,
  patchDefaultSucceeded,
  patchDefaultFailed,
  deleteDefaultSucceeded,
  deleteDefaultFailed,
  lookupLocationSucceeded,
  lookupLocationFailed,
  setPage,
} from './DefaultLocations.ducks';
import { GEO_LOGIC, RESOLUTION_CITY } from './constants';
import apiClient from 'helpers/apiClient';
import get from 'lodash/get';

// Shape an API error into either a string (banner) or {fieldErrors: {field: msg}}
// (per-field inline). Carmen returns 400s as a text/plain tapir sentence like
// "Invalid value for: body (error.expected.jsnumber at 'obj.resolution', Can't
// parse json as LatLon from \"...\" at 'obj.latlong')". We extract every
// `at 'obj.<field>'` marker and attribute the preceding clause to that field.
// Carmen's text/plain tapir error sentences are like:
//   Invalid value for: body (error.path.missing at 'obj.term', error.expected.jsnumber at 'obj.resolution')
// We scan for every `<reason> at 'obj.<field>'` clause; the reason is the run of
// non-comma characters preceding the marker (so multi-clause errors get clean
// per-field messages instead of one swallowing everything before it).
const FIELD_MARKER = /([^,()]+?)\s+at\s+'obj\.([^']+)'/g;

const HUMAN_REASON = {
  'error.path.missing': 'Required',
  'error.expected.jsstring': 'Must be a string',
  'error.expected.jsnumber': 'Must be a number',
  'error.expected.jsboolean': 'Must be a boolean',
  'error.expected.jsobject': 'Must be an object',
  'error.expected.jsarray': 'Must be an array',
};

const humanizeReason = (raw) => {
  const trimmed = raw.replace(/^[\s,()]+|[\s,()]+$/g, '').trim();
  if (HUMAN_REASON[trimmed]) {return HUMAN_REASON[trimmed];}
  // "Can't parse json as LatLon from \"...\"" → keep verbatim, just trim noise.
  return trimmed || 'Invalid';
};

const parseFieldErrors = (e, fallback) => {
  const status = get(e, 'response.status');
  const body = get(e, 'body');
  if (status !== 400) {return get(e, 'body.error', fallback);}

  // JSON shape (forward-compat): {errors: {field: msg|[msgs]}}.
  if (body && body.errors && typeof body.errors === 'object') {
    const fieldErrors = {};
    Object.keys(body.errors).forEach((field) => {
      const v = body.errors[field];
      if (Array.isArray(v)) {fieldErrors[field] = v.join(' ');}
      else if (typeof v === 'string') {fieldErrors[field] = v;}
    });
    if (Object.keys(fieldErrors).length > 0) {return { fieldErrors };}
  }

  // Text shape: tapir sentence (Carmen's actual 400 format).
  if (typeof body === 'string' && body.length > 0) {
    const fieldErrors = {};
    let match = FIELD_MARKER.exec(body);
    while (match !== null) {
      const field = match[2];
      const reason = humanizeReason(match[1]);
      if (!fieldErrors[field]) {fieldErrors[field] = reason;}
      match = FIELD_MARKER.exec(body);
    }
    if (Object.keys(fieldErrors).length > 0) {return { fieldErrors };}
    return body;
  }

  return get(e, 'body.error', fallback);
};

const fetchDefaultsSaga = function* () {
  try {
    const { platform, currentPage, pageSize } = yield select(state => state.DefaultLocations);
    const offset = (currentPage - 1) * pageSize;
    const payload = yield call(
      apiClient,
      `/carmen/defaults?platform=${platform}&offset=${offset}&limit=${pageSize}`
    );
    yield put(fetchDefaultsSucceeded(payload.locations, payload.total));
  } catch (e) {
    const error = get(e, 'body.error', 'Failed to fetch default locations');
    yield put(fetchDefaultsFailed(error));
    Account.toastrNotifications._errorMessage({
      error_title: '',
      error_message: 'There was a problem loading default locations.'
    });
  }
};

const createDefaultSaga = function* (action) {
  try {
    const username = yield select(state => state.AppUser.user.username);
    yield call(apiClient, '/carmen/defaults', {
      method: 'POST',
      headers: { 'X-Zignal-Username': username },
      body: JSON.stringify(action.payload),
    });
    const keepModalOpen = !!(action.meta && action.meta.keepModalOpen);
    yield put(createDefaultSucceeded(keepModalOpen));
    yield put(fetchDefaults());
    Account.toastrNotifications._successMessage({
      success_title: '',
      success_message: 'Default location created successfully!'
    });
  } catch (e) {
    const status = get(e, 'response.status');
    const error = status === 409
      ? 'Entry already exists for this term and platform'
      : parseFieldErrors(e, 'Failed to create default location');
    yield put(createDefaultFailed(error));
    if (typeof error === 'string') {
      Account.toastrNotifications._errorMessage({
        error_title: '',
        error_message: error,
      });
    }
  }
};

const patchDefaultSaga = function* (action) {
  try {
    const username = yield select(state => state.AppUser.user.username);
    const updated = yield call(apiClient, `/carmen/defaults/${action.payload.id}`, {
      method: 'PATCH',
      headers: { 'X-Zignal-Username': username },
      body: JSON.stringify(action.payload.updates),
    });
    yield put(patchDefaultSucceeded(updated));
    Account.toastrNotifications._successMessage({
      success_title: '',
      success_message: 'Default location updated successfully!'
    });
  } catch (e) {
    const error = parseFieldErrors(e, 'Failed to update default location');
    yield put(patchDefaultFailed(error));
    if (typeof error === 'string') {
      Account.toastrNotifications._errorMessage({
        error_title: '',
        error_message: 'There was a problem updating the default location.',
      });
    }
  }
};

const deleteDefaultSaga = function* (action) {
  try {
    yield call(apiClient, `/carmen/defaults/${action.payload}`, {
      method: 'DELETE',
    });
    yield put(deleteDefaultSucceeded());
    // Snap currentPage back if the deletion empties the current page; otherwise
    // refetch in place. Routing through setPage triggers fetchDefaults via setPageSaga.
    const { currentPage, pageSize, total } = yield select((s) => s.DefaultLocations);
    const newMax = Math.max(1, Math.ceil((total - 1) / pageSize));
    if (currentPage > newMax) {
      yield put(setPage(newMax));
    } else {
      yield put(fetchDefaults());
    }
    Account.toastrNotifications._successMessage({
      success_title: '',
      success_message: 'Default location deleted successfully!'
    });
  } catch (e) {
    const error = get(e, 'body.error', 'Failed to delete default location');
    yield put(deleteDefaultFailed(error));
    Account.toastrNotifications._errorMessage({
      error_title: '',
      error_message: 'There was a problem deleting the default location.'
    });
  }
};

const lookupLocationForwardSaga = function* (action) {
  try {
    const { location } = action.payload;
    const payload = yield call(apiClient, '/carmen/lookup/opencage/forward', {
      method: 'POST',
      body: JSON.stringify({ location, logic: GEO_LOGIC, resolution: RESOLUTION_CITY }),
    });
    yield put(lookupLocationSucceeded(payload.results || []));
  } catch (e) {
    const error = get(e, 'body.error', 'Failed to look up location');
    yield put(lookupLocationFailed(error));
  }
};

const lookupLocationReverseSaga = function* (action) {
  try {
    const { latitude, longitude } = action.payload;
    const payload = yield call(apiClient, '/carmen/lookup/opencage/reverse', {
      method: 'POST',
      body: JSON.stringify({
        latitude,
        longitude,
        logic: GEO_LOGIC,
        resolution: RESOLUTION_CITY,
      }),
    });
    yield put(lookupLocationSucceeded(payload.results || []));
  } catch (e) {
    const error = get(e, 'body.error', 'Failed to look up location');
    yield put(lookupLocationFailed(error));
  }
};

const setPlatformFilterSaga = function* (action) {
  if (action.payload !== '') {
    yield put(fetchDefaults());
  }
};

const setPageSaga = function* () {
  yield put(fetchDefaults());
};

const setPageSizeSaga = function* () {
  yield put(fetchDefaults());
};

const watchFetchDefaults = function* () {
  yield takeLatest(FETCH_DEFAULTS, fetchDefaultsSaga);
};

const watchCreateDefault = function* () {
  yield takeLatest(CREATE_DEFAULT, createDefaultSaga);
};

const watchPatchDefault = function* () {
  yield takeLatest(PATCH_DEFAULT, patchDefaultSaga);
};

const watchDeleteDefault = function* () {
  yield takeLatest(DELETE_DEFAULT, deleteDefaultSaga);
};

const watchLookupLocationForward = function* () {
  yield takeLatest(LOOKUP_LOCATION_FORWARD, lookupLocationForwardSaga);
};

const watchLookupLocationReverse = function* () {
  yield takeLatest(LOOKUP_LOCATION_REVERSE, lookupLocationReverseSaga);
};

const watchSetPlatformFilter = function* () {
  yield takeLatest(SET_PLATFORM_FILTER, setPlatformFilterSaga);
};

const watchSetPage = function* () {
  yield takeLatest(SET_PAGE, setPageSaga);
};

const watchSetPageSize = function* () {
  yield takeLatest(SET_PAGE_SIZE, setPageSizeSaga);
};

const sagas = function* () {
  yield all([
    fork(watchFetchDefaults),
    fork(watchCreateDefault),
    fork(watchPatchDefault),
    fork(watchDeleteDefault),
    fork(watchLookupLocationForward),
    fork(watchLookupLocationReverse),
    fork(watchSetPlatformFilter),
    fork(watchSetPage),
    fork(watchSetPageSize),
  ]);
};

export {
  fetchDefaultsSaga,
  createDefaultSaga,
  patchDefaultSaga,
  deleteDefaultSaga,
  lookupLocationForwardSaga,
  lookupLocationReverseSaga,
  setPlatformFilterSaga,
  setPageSaga,
  setPageSizeSaga,
  watchFetchDefaults,
  watchCreateDefault,
  watchPatchDefault,
  watchDeleteDefault,
  watchLookupLocationForward,
  watchLookupLocationReverse,
  watchSetPlatformFilter,
  watchSetPage,
  watchSetPageSize,
};

export default sagas;
